import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Badge, Form, Row, Col, Spinner, Pagination } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { listarActividadesConFiltros, obtenerTiposActividad } from '../../services/actividades/actividadService';
import { listarProveedores } from '../../services/proveedores/proveedorService';
import { getDataList } from '../../api/common';
import { getBadgeVencimiento, getDiasRestantesTexto } from '../../helpers/actividadVencimiento';
import Swal from 'sweetalert2';
import ModalEditarActividad from '../../components/actividades/ModalEditarActividad';
import ModalHistorialActividad from '../../components/actividades/ModalHistorialActividad';
import ModalConfirmarRequisicion from '../../components/actividades/ModalConfirmarRequisicion';
import capitalizeText from '../../utils/capitalizeText';

export const ActividadesAbiertas = ({ refresh, setRefresh }) => {
  const navigate = useNavigate();
  
  // Estados para datos
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actividadesSeleccionadas, setActividadesSeleccionadas] = useState([]);
  
  // Estados para filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [filtros, setFiltros] = useState({
    vencimiento: '',
    equipo: '',
    proveedor: '',
    fechaDesde: '',
    fechaHasta: '',
    tipoActividad: ''
  });
  
  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina] = useState(20);
  
  // Estados para modales
  const [modalEditar, setModalEditar] = useState(false);
  const [modalHistorial, setModalHistorial] = useState(false);
  const [modalRequisicion, setModalRequisicion] = useState(false);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  
  // Estados para selects
  const [equipos, setEquipos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [tiposActividad, setTiposActividad] = useState([]);

  // Cargar datos iniciales
  useEffect(() => {
    cargarEquipos();
    cargarProveedores();
    cargarTiposActividad();
  }, []);

  useEffect(() => {
    cargarActividades();
  }, [filtros, refresh]);

  const cargarEquipos = async () => {
    try {
      const response = await getDataList('Patrones');
      if (response?.data.data) {
        const options = response.data.data
          .map(eq => ({ value: eq._id, label: eq.item }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setEquipos(options);
      }
    } catch (error) {
      console.error('Error al cargar equipos:', error);
    }
  };

  const cargarProveedores = async () => {
    try {
      const response = await listarProveedores();
      if (response?.data.data) {
        const options = response.data.data
          .filter(p => p.estado === 'Activo')
          .map(p => ({ value: p._id, label: p.nombre }))
          .sort((a, b) => a.label.localeCompare(b.label));

          console.log('Opciones de proveedores:', options);
        setProveedores(options);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const cargarTiposActividad = async () => {
    // Catálogo "oficial" según el formulario de crear actividad
    // (Form_2EqPatron.js). Si la BD trae tipos adicionales (legacy u otros),
    // se agregan después vía /Actividad/tipos.
    const oficiales = [
      'Calibración',
      'Comprobación Intermedia',
      'Mantenimiento Correctivo',
      'Mantenimiento Preventivo'
    ];
    const set = new Set(oficiales);
    try {
      const res = await obtenerTiposActividad();
      const extras = res?.data?.data || res?.data || [];
      if (Array.isArray(extras)) extras.forEach(t => t && set.add(t));
    } catch (err) {
      console.error('Error al cargar tipos de actividad:', err);
    }
    const tipos = Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map(t => ({ value: t, label: t }));
    setTiposActividad(tipos);
  };

  const cargarActividades = async () => {
    try {
      setLoading(true);
      const filtrosLimpios = Object.fromEntries(
        Object.entries(filtros).filter(([_, v]) => v !== '')
      );
      filtrosLimpios.estado = 'Abierta';
      
      const response = await listarActividadesConFiltros(filtrosLimpios);

      console.log('Respuesta del backend:', response);
      if (response?.data.data) {
        // Asegurar que siempre sea un array
        const actividadesData = Array.isArray(response.data.data) ? response.data.data : [];
        setActividades(actividadesData);
        setPaginaActual(1);
        setActividadesSeleccionadas([]);
      }
    } catch (error) {
      console.error('Error al cargar actividades:', error);
      Swal.fire('Error', 'Error al cargar actividades abiertas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      vencimiento: '',
      equipo: '',
      proveedor: '',
      fechaDesde: '',
      fechaHasta: '',
      tipoActividad: ''
    });
  };

  const toggleSeleccionActividad = (actividadId) => {
    setActividadesSeleccionadas(prev => {
      if (prev.includes(actividadId)) {
        return prev.filter(id => id !== actividadId);
      } else {
        return [...prev, actividadId];
      }
    });
  };

  const toggleSeleccionTodos = () => {
    if (actividadesSeleccionadas.length === actividadesPaginadas.length) {
      setActividadesSeleccionadas([]);
    } else {
      setActividadesSeleccionadas(actividadesPaginadas.map(a => a._id));
    }
  };

  const handleEditarActividad = (actividad) => {
    setActividadSeleccionada(actividad);
    setModalEditar(true);
  };

  const handleVerHistorial = (actividad) => {
    setActividadSeleccionada(actividad);
    setModalHistorial(true);
  };

  const handleCrearRequisicion = () => {
    if (actividadesSeleccionadas.length === 0) {
      Swal.fire('Advertencia', 'Selecciona al menos una actividad para crear una requisición', 'warning');
      return;
    }
    setModalRequisicion(true);
  };

  // Paginación
  const actividadesArray = Array.isArray(actividades) ? actividades : [];
  const indexUltimoItem = paginaActual * itemsPorPagina;
  const indexPrimerItem = indexUltimoItem - itemsPorPagina;
  const actividadesPaginadas = actividadesArray.slice(indexPrimerItem, indexUltimoItem);
  const totalPaginas = Math.ceil(actividadesArray.length / itemsPorPagina);

  console.log('Actividades cargadas:', actividadesPaginadas);
  const opcionesVencimiento = [
    { value: '', label: 'Todos' },
    { value: 'vencida', label: 'Vencidas' },
    { value: 'este_mes', label: 'Este mes' },
    { value: 'menos_1_mes', label: 'Próximo mes' },
    { value: '1_a_3_meses', label: '1-3 meses' },
    { value: 'al_dia', label: 'Al día' }
  ];

  return (
    <div className="container-fluid">
      <Card className="w-100 mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center w-100">
          <h5 className="mb-0">Actividades Abiertas</h5>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
          >
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'} Filtros
          </Button>
        </Card.Header>

        {mostrarFiltros && (
          <Card.Body className='w-100'>
            <Row>
              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Vencimiento</Form.Label>
                <Select
                  options={opcionesVencimiento}
                  value={opcionesVencimiento.find(o => o.value === filtros.vencimiento)}
                  onChange={(option) => handleFiltroChange('vencimiento', option?.value || '')}
                  placeholder="Seleccionar..."
                  isClearable
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Equipo</Form.Label>
                <Select
                  options={equipos}
                  value={equipos.find(e => e.value === filtros.equipo)}
                  onChange={(option) => handleFiltroChange('equipo', option?.value || '')}
                  placeholder="Buscar equipo..."
                  isClearable
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Proveedor</Form.Label>
                <Select
                  options={proveedores}
                  value={proveedores.find(p => p.value === filtros.proveedor)}
                  onChange={(option) => handleFiltroChange('proveedor', option?.value || '')}
                  placeholder="Buscar proveedor..."
                  isClearable
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Tipo de Actividad</Form.Label>
                <Select
                  options={tiposActividad}
                  value={tiposActividad.find(t => t.value === filtros.tipoActividad)}
                  onChange={(option) => handleFiltroChange('tipoActividad', option?.value || '')}
                  placeholder="Seleccionar..."
                  isClearable
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Fecha Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.fechaDesde}
                  onChange={(e) => handleFiltroChange('fechaDesde', e.target.value)}
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3">
                <Form.Label>Fecha Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.fechaHasta}
                  onChange={(e) => handleFiltroChange('fechaHasta', e.target.value)}
                />
              </Col>

              <Col xs={12} md={6} lg={3} className="mb-3 d-flex align-items-end">
                <Button
                  variant="secondary"
                  className="w-100"
                  onClick={limpiarFiltros}
                >
                  Limpiar Filtros
                </Button>
              </Col>
            </Row>
          </Card.Body>
        )}
      </Card>

      <Card className="w-100">
        <Card.Body className="p-0 w-100">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Cargando actividades...</p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">
                  {actividadesArray.length} actividad{actividadesArray.length !== 1 ? 'es' : ''} encontrada{actividadesArray.length !== 1 ? 's' : ''}
                  {actividadesSeleccionadas.length > 0 && (
                    <> · <strong>{actividadesSeleccionadas.length}</strong> seleccionada{actividadesSeleccionadas.length !== 1 ? 's' : ''}</>
                  )}
                </span>
              </div>

              <div className="table-responsive w-100">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={actividadesSeleccionadas.length === actividadesPaginadas.length && actividadesPaginadas.length > 0}
                          onChange={toggleSeleccionTodos}
                        />
                      </th>
                      <th>Equipo</th>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Proveedor</th>
                      <th>Fecha Inicio</th>
                      <th>Fecha Vencimiento</th>
                      <th>Estado Vencimiento</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {actividadesPaginadas.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          No se encontraron actividades abiertas
                        </td>
                      </tr>
                    ) : (
                      actividadesPaginadas.map((actividad) => {
                        const badgeInfo = getBadgeVencimiento(actividad.FechaVencimiento);
                        const diasTexto = getDiasRestantesTexto(actividad.FechaVencimiento);
                        
                        return (
                          <tr key={actividad._id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={actividadesSeleccionadas.includes(actividad._id)}
                                onChange={() => toggleSeleccionActividad(actividad._id)}
                                disabled={actividad.requisicionId}
                              />
                            </td>
                            {/* Arriba el item del equipo, si no existe mostrar 'N/A', abajo marca serial inventario */}
                            <td>{capitalizeText(actividad.equipo?.item) || 'N/A'}</td>
                            <td>
                              {actividad.equipo?.marca || 'N/A'} - {actividad.equipo?.modelo || ''} 
                              <smal>
                                <div className="text-muted small">
                                  {`SN: ${actividad.equipo?.serial || 'N/A'} / Inv: ${actividad.equipo?.inventario || 'N/A'}`}
                                </div>
                              </smal>
                            </td>
                            <td>{actividad.tipoActividad}</td>
                            <td>{actividad.proveedorRef?.nombre || 'Sin asignar'}</td>
                            <td>{moment(actividad.FechaInicio).format('DD/MM/YYYY')}</td>
                            <td>{moment(actividad.FechaVencimiento).format('DD/MM/YYYY')}</td>
                            <td>
                              <Badge bg={badgeInfo.variant}>
                                {badgeInfo.label}
                              </Badge>
                              <div className="small text-muted">{diasTexto}</div>
                              {actividad.requisicionId && (
                                <Badge bg="secondary" className="ms-2">En Requisición</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEditarActividad(actividad)}
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => handleVerHistorial(actividad)}
                              >
                                <i className="bi bi-clock-history"></i>
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {totalPaginas > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setPaginaActual(1)}
                      disabled={paginaActual === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setPaginaActual(prev => Math.max(1, prev - 1))}
                      disabled={paginaActual === 1}
                    />
                    
                    {[...Array(totalPaginas)].map((_, idx) => {
                      const numeroPagina = idx + 1;
                      if (
                        numeroPagina === 1 ||
                        numeroPagina === totalPaginas ||
                        (numeroPagina >= paginaActual - 2 && numeroPagina <= paginaActual + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={numeroPagina}
                            active={numeroPagina === paginaActual}
                            onClick={() => setPaginaActual(numeroPagina)}
                          >
                            {numeroPagina}
                          </Pagination.Item>
                        );
                      } else if (
                        numeroPagina === paginaActual - 3 ||
                        numeroPagina === paginaActual + 3
                      ) {
                        return <Pagination.Ellipsis key={numeroPagina} disabled />;
                      }
                      return null;
                    })}

                    <Pagination.Next
                      onClick={() => setPaginaActual(prev => Math.min(totalPaginas, prev + 1))}
                      disabled={paginaActual === totalPaginas}
                    />
                    <Pagination.Last
                      onClick={() => setPaginaActual(totalPaginas)}
                      disabled={paginaActual === totalPaginas}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Barra de acciones flotante */}
      {actividadesSeleccionadas.length > 0 && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 1000,
            backgroundColor: 'rgb(34, 171, 180)',
            color: 'white',
            padding: '15px 25px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <span><strong>{actividadesSeleccionadas.length}</strong> actividad{actividadesSeleccionadas.length !== 1 ? 'es' : ''} seleccionada{actividadesSeleccionadas.length !== 1 ? 's' : ''}</span>
            <Button
              variant="light"
              size="sm"
              onClick={handleCrearRequisicion}
            >
              <i className="bi bi-clipboard-check me-2"></i>
              Crear Requisición
            </Button>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setActividadesSeleccionadas([])}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Modales */}
      {modalEditar && (
        <ModalEditarActividad
          show={modalEditar}
          onHide={() => setModalEditar(false)}
          actividad={actividadSeleccionada}
          onActualizar={() => {
            setRefresh(!refresh);
            setModalEditar(false);
          }}
        />
      )}

      {modalHistorial && (
        <ModalHistorialActividad
          show={modalHistorial}
          onHide={() => setModalHistorial(false)}
          actividad={actividadSeleccionada}
        />
      )}

      {modalRequisicion && (
        <ModalConfirmarRequisicion
          show={modalRequisicion}
          onHide={() => setModalRequisicion(false)}
          actividadesIds={actividadesSeleccionadas}
          actividades={actividadesArray.filter(a => actividadesSeleccionadas.includes(a._id))}
          onCrear={() => {
            setActividadesSeleccionadas([]);
            setRefresh(!refresh);
            setModalRequisicion(false);
          }}
        />
      )}
    </div>
  );
};
